import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLoadGkvBillsForEdit } from "../../../../../../stores/Transactions/insurance-query";
import { useUpdateGkvBill } from "../../../../../../stores/Transactions/insurance-mutation";
import { insuranceTransactionKeys } from "../../../../../../stores/Transactions/insurance-transaction.keys";
import { useApi } from "../../../../../../api/ApiProvider";
import { GkvBillEditDto, DirectBillingRxDto } from "../types/interface";
import notificationStore from "../../../../../../stores/Notification/notificationStore";

export interface UseGkvEditBillsReturn {
  bills: GkvBillEditDto[];
  isLoading: boolean;
  refetch: () => void;
  removeBill: (billId: string) => Promise<void>;
  dismissBill: (billId: string) => void;
  removeRx: (billId: string, rxId: string) => Promise<void>;
  bulkRemoveRxs: (rxIds: Set<string>) => Promise<void>;
  addRx: (billId: string, rx: DirectBillingRxDto) => Promise<void>;
  updateBillLocally: (billId: string, partial: Partial<GkvBillEditDto>) => void;
}

/**
 * Wraps `useLoadGkvBillsForEdit` with local optimistic state.
 *
 * - Server data comes from TanStack Query (no caching, always fresh on mount).
 * - Local overrides allow optimistic UI updates (remove Rx, patch PDFs).
 * - On error, reverts to server data via refetch.
 */
export function useGkvEditBills(billIds: string[]): UseGkvEditBillsReturn {
  const { transaction } = useApi();
  const qc = useQueryClient();
  const updateGkvBill = useUpdateGkvBill();

  const { data: serverBills, isLoading, refetch } = useLoadGkvBillsForEdit(billIds);

  // Local state overrides server data for optimistic updates
  const [localBills, setLocalBills] = useState<GkvBillEditDto[] | null>(null);

  // Use local if set, else server data, else empty
  const bills: GkvBillEditDto[] = useMemo(
    () => localBills ?? (serverBills as GkvBillEditDto[] | undefined) ?? [],
    [localBills, serverBills],
  );

  // Sync from server when data arrives and no local override yet
  // (localBills is set only after first user action)

  /**
   * Dismiss a bill from the current editing session (UI only).
   * The bill remains in the DB — it is NOT deleted or modified.
   * Use this for "Ganz entfernen" to exclude a bill from the current export batch.
   */
  const dismissBill = useCallback(
    (billId: string) => {
      setLocalBills(bills.filter((b) => b.id !== billId));
    },
    [bills],
  );

  /**
   * Soft-delete an entire bill. Optimistic removal + backend call.
   */
  const removeBill = useCallback(
    async (billId: string) => {
      const prev = bills;
      // Optimistic: remove from UI
      setLocalBills(bills.filter((b) => b.id !== billId));

      try {
        await transaction.softDeleteInsuranceTransaction(billId);
        // Invalidate so refetch excludes deleted bill
        qc.invalidateQueries({
          queryKey: insuranceTransactionKeys.gkvBillEdit.forBill(billId),
        });
      } catch (error) {
        // Revert on failure
        setLocalBills(prev);
        notificationStore.showMessage("Fehler beim Löschen der Abrechnung", "error");
        throw error;
      }
    },
    [bills, transaction, qc],
  );

  /**
   * Remove a single Rx from a bill. Optimistic + backend save.
   * If the bill becomes empty after removal, soft-delete it.
   */
  const removeRx = useCallback(
    async (billId: string, rxId: string) => {
      const prev = bills;
      const bill = bills.find((b) => b.id === billId);
      if (!bill) return;

      const remainingRxs = bill.rxs.filter((rx) => rx.id !== rxId);

      if (remainingRxs.length === 0) {
        // Bill would be empty — soft delete instead
        await removeBill(billId);
        return;
      }

      // Optimistic: update Rx list + recalculate amount
      const newAmount = remainingRxs.reduce((sum, rx) => sum + (rx.price?.netValue ?? 0), 0);
      setLocalBills(
        bills.map((b) => (b.id === billId ? { ...b, rxs: remainingRxs, amount: newAmount } : b)),
      );

      try {
        await updateGkvBill.mutateAsync({
          billId,
          dto: {
            rxIds: remainingRxs.map((rx) => rx.id),
            amount: newAmount,
          },
        });
      } catch (error) {
        setLocalBills(prev);
        notificationStore.showMessage("Fehler beim Entfernen des Rezepts", "error");
        throw error;
      }
    },
    [bills, removeBill, updateGkvBill],
  );

  /**
   * Bulk-remove multiple Rxs across bills in one pass.
   * Groups by bill, computes remaining Rxs per bill, and fires one update per bill.
   * Bills that become empty are soft-deleted.
   */
  const bulkRemoveRxs = useCallback(
    async (rxIds: Set<string>) => {
      if (rxIds.size === 0) return;

      const prev = bills;

      // Group rxIds by bill
      const billUpdates = new Map<string, string[]>(); // billId → rxIds to remove
      rxIds.forEach((rxId) => {
        const bill = bills.find((b) => b.rxs.some((rx) => rx.id === rxId));
        if (bill) {
          const existing = billUpdates.get(bill.id) || [];
          existing.push(rxId);
          billUpdates.set(bill.id, existing);
        }
      });

      // Compute new state optimistically
      const billsToDelete: string[] = [];
      const updatedBills = bills
        .map((b) => {
          const removeIds = billUpdates.get(b.id);
          if (!removeIds) return b;
          const remainingRxs = b.rxs.filter((rx) => !removeIds.includes(rx.id));
          if (remainingRxs.length === 0) {
            billsToDelete.push(b.id);
            return null; // Will be filtered out
          }
          const newAmount = remainingRxs.reduce((sum, rx) => sum + (rx.price?.netValue ?? 0), 0);
          return { ...b, rxs: remainingRxs, amount: newAmount };
        })
        .filter((b): b is GkvBillEditDto => b !== null);

      // Optimistic UI update
      setLocalBills(updatedBills);

      try {
        // Persist: delete empty bills
        await Promise.all(
          billsToDelete.map((billId) => transaction.softDeleteInsuranceTransaction(billId)),
        );
        // Persist: update bills that still have Rxs
        await Promise.all(
          updatedBills
            .filter((b) => billUpdates.has(b.id))
            .map((b) =>
              updateGkvBill.mutateAsync({
                billId: b.id,
                dto: {
                  rxIds: b.rxs.map((rx) => rx.id),
                  amount: b.amount,
                },
              }),
            ),
        );
      } catch (error) {
        setLocalBills(prev);
        notificationStore.showMessage("Fehler beim Entfernen der Rezepte", "error");
      }
    },
    [bills, transaction, updateGkvBill],
  );

  /**
   * Add a single Rx to a bill. Optimistic update + backend persist.
   */
  const addRx = useCallback(
    async (billId: string, rx: DirectBillingRxDto) => {
      const bill = bills.find((b) => b.id === billId);
      if (!bill) return;
      // Prevent duplicates
      if (bill.rxs.some((existing) => existing.id === rx.id)) return;

      const prev = bills;
      const updatedRxs = [...bill.rxs, rx];
      const newAmount = updatedRxs.reduce((sum, r) => sum + (r.price?.netValue ?? 0), 0);

      // Optimistic update
      setLocalBills(
        bills.map((b) => (b.id === billId ? { ...b, rxs: updatedRxs, amount: newAmount } : b)),
      );

      try {
        await updateGkvBill.mutateAsync({
          billId,
          dto: {
            rxIds: updatedRxs.map((r) => r.id),
            amount: newAmount,
          },
        });
      } catch (error) {
        // Revert on failure
        setLocalBills(prev);
        notificationStore.showMessage("Fehler beim Hinzufügen des Rezepts", "error");
        throw error;
      }
    },
    [bills, updateGkvBill],
  );

  /**
   * Patch a bill's local data (e.g., after print returns PDFs).
   * Uses functional updater to avoid stale closure when called multiple times in a loop.
   */
  const updateBillLocally = useCallback(
    (billId: string, partial: Partial<GkvBillEditDto>) => {
      setLocalBills((prev) => {
        const current = prev ?? bills;
        return current.map((b) => (b.id === billId ? { ...b, ...partial } : b));
      });
    },
    [bills],
  );

  return {
    bills,
    isLoading,
    refetch,
    removeBill,
    dismissBill,
    removeRx,
    bulkRemoveRxs,
    addRx,
    updateBillLocally,
  };
}
